import { zodResolver } from "@hookform/resolvers/zod";
import { Resolver, SubmitHandler, useForm } from "react-hook-form";
import { TSiteVisitSchema, SiteVisitSchema } from "../Models";
import { Button } from "@/components/ui/button";
import { Dispatch, JSX, SetStateAction, useState } from "react";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SiteVisit, TenderDurationFragment } from "@/graphql/__generated__/graphql";
import { useTranslation } from "react-i18next";
import SiteVisitFormInputs from "./Shared/SiteVisitFormInputs";
import { convertDecimalToBp } from "@/utils/helpers";
import { convertBpToDecimal } from "@/utils/helpers";
import { CircleLoader } from "@/components/shared/loaders";

interface Props {
  onEditSiteVisit: (_siteVisit: TSiteVisitSchema, _selectedSiteVisit: SiteVisit, _setShowEditSiteVisitModal: Dispatch<SetStateAction<boolean>>) => Promise<void>;
  tenderDuration: TenderDurationFragment;
  currentSiteVisit: SiteVisit;
  showSiteVisitModal: boolean;
  setShowSiteVisitModal: Dispatch<SetStateAction<boolean>>;
  isNewService: boolean;
  isProcessing?: boolean;
};

/**
 * Renders the form for adding a site visit.
 * Wrapped in a Dialog component.
 *
 * @param {Props} props - The component props.
 * @returns {JSX.Element} The rendered component.
 */
const EditSiteVisitComponent = (props: Props): JSX.Element => {
  const {
    onEditSiteVisit,
    tenderDuration,
    currentSiteVisit,
    showSiteVisitModal,
    setShowSiteVisitModal,
    isNewService,
    isProcessing
  } = props;

  const form = useForm<TSiteVisitSchema>({
    resolver: zodResolver(SiteVisitSchema) as Resolver<TSiteVisitSchema>,
    defaultValues: {
      ...currentSiteVisit,
      margin: isNewService ? currentSiteVisit.margin : convertBpToDecimal(currentSiteVisit.margin),
      startDate: currentSiteVisit.startDate ? new Date(currentSiteVisit.startDate) : undefined,
      endDate: currentSiteVisit.endDate ? new Date(currentSiteVisit.endDate) : undefined,
    },
    mode: "onChange",
  });

  const { t } = useTranslation(["modals", "common"]);

  const [useOptionalDates, setUseOptionalDates] = useState(!!(currentSiteVisit.startDate || currentSiteVisit.endDate));

  const onInvalid = (errors: unknown) => console.error(errors);

  /**
   * OnSubmit handler for the form.
   * @param {TSiteVisitSchema} data
   */
  const onSubmit: SubmitHandler<TSiteVisitSchema> = async (data: TSiteVisitSchema) => {
    if (isNewService) {
      await onEditSiteVisit(data, currentSiteVisit, setShowSiteVisitModal);
    } else {
      // Create a new site visit object with the modified dates
      const modifiedData = {
        ...data,
        margin: convertDecimalToBp(data.margin),
        startDate: useOptionalDates ? data.startDate : undefined,
        endDate: useOptionalDates ? data.endDate : undefined
      };

      await onEditSiteVisit(modifiedData, currentSiteVisit, setShowSiteVisitModal);
    }
  };

  return (
    <Dialog open={showSiteVisitModal} onOpenChange={setShowSiteVisitModal}>
      <DialogContent className="select-none overflow-y-auto max-w-[90vw] lg:max-w-fit max-h-[90vh] h-full md:h-auto">
        <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="space-y-6" id="add-site-visit-form" >
          <DialogHeader className="text-left">
            <DialogTitle>{t("addsitevisit.title")}</DialogTitle>
            <DialogDescription>
              {t("addsitevisit.description")}
            </DialogDescription>
          </DialogHeader>

          <SiteVisitFormInputs
            form={form}
            tenderDuration={tenderDuration}
            setUseOptionalDates={setUseOptionalDates}
          />

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={isProcessing}>
                {t("common:buttons.close")}
              </Button>
            </DialogClose>
            <Button variant="default" type="submit" disabled={isProcessing} className="gap-2">
              <CircleLoader visible={isProcessing} height={16} width={16} strokeWidth={2} />
              {t("common:buttons.edit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditSiteVisitComponent;
